// ============================================================================
// LE SÉLECTEUR DE PLAGE DE DATES — « du … au … », avec ses raccourcis.
//
// ⚠️ IL NE CALCULE AUCUNE DATE, et c'est sa règle de conception. Les raccourcis lui sont DONNÉS par
// l'appelant, bornes comprises : « 7 derniers jours » se calcule déjà dans chaque app pour piloter ses
// crans, et en refaire ici une seconde définition, c'est se garantir qu'un jour la même étiquette
// désignera deux plages différentes selon le bouton cliqué. Ce composant rend une plage et propose des
// plages ; il n'en invente pas.
//
// ⚠️ IL S'ADOSSE À `Popover` : Échap, clic extérieur, focus rendu au déclencheur, bornage à la fenêtre
// et fermeture au défilement sont déjà tenus là-bas. Un panneau ancré réécrit à la main, c'est réécrire
// les oublis que `popover.tsx` documente.
//
// ⚠️ TOUT EST EN ISO (`AAAA-MM-JJ`) : c'est ce que `<input type="date">` lit et écrit nativement.
// Une app qui stocke ses bornes autrement (le back-office les compacte en `AAAAMMJJ`) convertit chez
// elle — plutôt que d'imposer un format à toutes les autres.
// ============================================================================
import { useState } from 'react';
import { DATE_RANGE, DATE_RANGE_SHORTCUT_TONES } from '@orgazone/ui-tokens';
import { dateLong } from '@orgazone/date-format';
import { Icon } from './icons';
import { Popover } from './popover';

/** Une plage, bornes INCLUSES, en ISO. */
export interface DateRange {
  from: string;
  to: string;
}

export interface DateRangeShortcut extends DateRange {
  id: string;
  label: string;
}

export interface DateRangePickerProps {
  /** La plage en vigueur, ou `null` quand c'est un autre contrôle qui pilote l'écran. */
  value: DateRange | null;
  /** ⚠️ `null` = « rends la main » : l'appelant revient à ses crans. Ce n'est pas une plage vide. */
  onChange: (v: DateRange | null) => void;
  /** Les plages proposées d'un clic, bornes comprises — calculées par l'appelant (voir en-tête). */
  shortcuts: readonly DateRangeShortcut[];
  /** Ce qu'annonce le déclencheur quand aucune plage n'est posée. */
  placeholder?: string;
  /** Ce que le pied dit quand aucune plage n'est posée : QUI pilote l'écran à la place. */
  idleHint: string;
  /** Verbe de l'abandon — il doit nommer ce vers quoi on revient, pas « Annuler ». */
  clearLabel?: string;
  /** Bornes acceptables des champs libres (ISO). `max` vaut typiquement aujourd'hui. */
  min?: string;
  max?: string;
  label?: string;
}

/** Nombre de jours d'une plage, bornes INCLUSES — « du 1er au 1er » fait 1 jour, pas 0. */
export function rangeDays(from: string, to: string): number {
  const t = (v: string): number => {
    const [a, m, j] = v.split('-').map(Number);
    return a && m && j ? new Date(a, m - 1, j).getTime() : Number.NaN;
  };
  const d = (t(to) - t(from)) / 86_400_000;
  return Number.isFinite(d) ? Math.round(d) + 1 : 0;
}

/**
 * Le résumé de ce qui est demandé, en toutes lettres.
 * ⚠️ C'EST LA SEULE RELECTURE POSSIBLE avant de fermer le panneau : deux champs `AAAA-MM-JJ` mal
 * saisis se ressemblent, « 3 jours » et « 33 jours » non. Une plage à l'envers se DIT plutôt que de
 * rendre un décompte négatif que personne ne saurait interpréter.
 */
export function resumeDePlage(v: DateRange | null, idleHint: string): string {
  if (!v) return idleHint;
  if (!v.from || !v.to) return 'Choisissez les deux bornes';
  if (v.from > v.to) return 'La date de début est postérieure à la date de fin';
  if (v.from === v.to) return `Journée du ${dateLong(v.from)}`;
  return `${rangeDays(v.from, v.to)} jours — du ${dateLong(v.from)} au ${dateLong(v.to)}`;
}

/** L'étiquette du déclencheur : le raccourci reconnu, sinon les bornes, sinon l'invite. */
export function rangeLabel(
  v: DateRange | null,
  shortcuts: readonly DateRangeShortcut[],
  placeholder: string,
): string {
  if (!v) return placeholder;
  // ⚠️ Le NOM du raccourci l'emporte sur les dates : « Ce mois-ci » se relit mieux que ses bornes, et
  // c'est ce qu'on vient de cliquer.
  const connu = shortcuts.find((s) => s.from === v.from && s.to === v.to);
  if (connu) return connu.label;
  if (v.from === v.to) return dateLong(v.from);
  return `${dateLong(v.from)} → ${dateLong(v.to)}`;
}

/**
 * CE QUE LE BOUTON DE PIED VA FAIRE — et donc comment il doit s'appeler.
 *
 * ⚠️ UN BOUTON DOIT NOMMER SON ACTION. « Fermer » posé sur une saisie complète était faux : le
 * panneau APPLIQUE la plage en se refermant, et rien ne l'annonçait — on cliquait « Fermer » en
 * croyant abandonner, et l'écran changeait de période.
 * ⚠️ UNE SAISIE INCOMPLÈTE OU INVERSÉE GARDE « Fermer », parce qu'elle sera bel et bien abandonnée :
 * l'inverse promettrait une validation qui n'aura pas lieu. Le pied dit alors ce qui manque.
 */
export function actionDeFermeture(brouillon: DateRange | null, complete: boolean): 'valider' | 'fermer' {
  return brouillon !== null && complete ? 'valider' : 'fermer';
}

export function DateRangePicker({
  value, onChange, shortcuts, placeholder = 'Dates personnalisées', idleHint,
  clearLabel = 'Revenir aux crans', min, max, label = 'Choisir une plage de dates',
}: DateRangePickerProps) {
  const [ouvert, setOuvert] = useState(false);
  // ⚠️ SAISIE EN COURS TENUE À PART : taper la borne de début produit fatalement, pendant une frappe,
  // une plage inversée ou incomplète. L'appliquer à l'écran à chaque touche déclencherait des lectures
  // sur des périodes que personne n'a demandées — et ferait clignoter des chiffres faux.
  const [brouillon, setBrouillon] = useState<DateRange | null>(null);
  const current = brouillon ?? value;
  const complete = !!current && !!current.from && !!current.to && current.from <= current.to;

  const t = DATE_RANGE.trigger;

  const saisir = (p: Partial<DateRange>) =>
    setBrouillon({ from: current?.from ?? '', to: current?.to ?? '', ...p });

  /**
   * Ce que devient la saisie quand le panneau se ferme — PAR N'IMPORTE QUEL CHEMIN.
   * ⚠️ « Fermer », Échap et le clic extérieur font donc la MÊME chose. Une saisie appliquée par le
   * bouton mais perdue par un clic à côté serait une règle invisible : on aurait tapé deux dates
   * justes et l'écran n'aurait pas bougé, sans un mot.
   * ⚠️ EN REVANCHE UNE SAISIE INCOMPLÈTE OU INVERSÉE EST ABANDONNÉE : elle interrogerait une période
   * absurde, et l'écran répondrait « aucune vente » à une question mal posée.
   */
  const validate = () => {
    if (brouillon && complete) onChange(brouillon);
    setBrouillon(null);
  };

  return (
    <Popover
      open={ouvert}
      onOpenChange={(o) => { setOuvert(o); if (!o) validate(); }}
      id="oz-date-range"
      label={label}
      role="dialog"
      // ⚠️ `start` : le panneau s'aligne sur la GAUCHE du déclencheur (défaut du socle : `end`). Il est
      // posé en début de barre, sous les crans — aligné à droite, il partirait vers le bord de l'écran.
      geom={{ width: DATE_RANGE.panel.width, align: 'start', offsetY: DATE_RANGE.panel.offsetY }}
      trigger={(api) => (
        <button
          {...api}
          type="button"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: t.gap, flexShrink: 0,
            height: t.height, padding: `0 ${t.padH}px`, cursor: 'pointer',
            border: '1px solid var(--ink-200)', borderRadius: `var(--r-${t.radius})`,
            background: 'var(--paper)', color: 'var(--ink-800)',
            fontSize: t.fontSize, fontWeight: t.weight, whiteSpace: 'nowrap',
          }}
        >
          <Icon name="calendar" size={t.icon} stroke={1.75} />
          {rangeLabel(value, shortcuts, placeholder)}
        </button>
      )}
    >
      {({ close }) => (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: `repeat(${DATE_RANGE.shortcuts.cols}, 1fr)`,
            gap: DATE_RANGE.shortcuts.gap, padding: DATE_RANGE.shortcuts.pad,
          }}>
            {shortcuts.map((s) => {
              const actif = !!value && value.from === s.from && value.to === s.to;
              const ton = actif ? DATE_RANGE_SHORTCUT_TONES.on : DATE_RANGE_SHORTCUT_TONES.off;
              return (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={actif}
                  onClick={() => { setBrouillon(null); onChange({ from: s.from, to: s.to }); close(); }}
                  style={{
                    height: DATE_RANGE.shortcuts.height, padding: `0 ${DATE_RANGE.shortcuts.padH}px`,
                    border: `1px solid var(--${ton.border})`, borderRadius: `var(--r-${DATE_RANGE.shortcuts.radius})`,
                    background: `var(--${ton.bg})`, color: `var(--${ton.fg})`,
                    fontSize: DATE_RANGE.shortcuts.fontSize, fontWeight: DATE_RANGE.shortcuts.weight,
                    cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          <div style={{
            display: 'flex', gap: DATE_RANGE.bornes.gap, alignItems: 'flex-end',
            padding: `${DATE_RANGE.bornes.padV}px ${DATE_RANGE.bornes.padH}px`,
            borderTop: '1px solid var(--ink-100)',
          }}>
            <Borne libelle="Du" value={current?.from ?? ''} min={min} max={max} onChange={(from) => saisir({ from })} />
            <Borne libelle="Au" value={current?.to ?? ''} min={min} max={max} onChange={(to) => saisir({ to })} />
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: DATE_RANGE.foot.gap,
            padding: `${DATE_RANGE.foot.padV}px ${DATE_RANGE.foot.padH}px`,
            borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)',
          }}>
            <span style={{ flex: 1, fontSize: DATE_RANGE.foot.hintSize, color: 'var(--ink-500)', lineHeight: 1.4 }}>
              {resumeDePlage(current, idleHint)}
            </span>
            {value ? (
              <button
                type="button"
                onClick={() => { setBrouillon(null); onChange(null); close(); }}
                style={{
                  height: DATE_RANGE.foot.btnHeight, padding: `0 ${DATE_RANGE.foot.ghostPadH}px`,
                  border: '1px solid var(--ink-200)', borderRadius: `var(--r-${DATE_RANGE.foot.radius})`,
                  background: 'var(--paper)', color: 'var(--ink-700)',
                  fontSize: DATE_RANGE.foot.fontSize, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {clearLabel}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => { validate(); close(); }}
              style={{
                height: DATE_RANGE.foot.btnHeight, padding: `0 ${DATE_RANGE.foot.btnPadH}px`,
                border: 'none', borderRadius: `var(--r-${DATE_RANGE.foot.radius})`,
                background: 'var(--teal-500)', color: '#fff',
                fontSize: DATE_RANGE.foot.fontSize, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {actionDeFermeture(brouillon, complete) === 'valider' ? 'Valider' : 'Fermer'}
            </button>
          </div>
        </>
      )}
    </Popover>
  );
}

/** Une borne libre. ⚠️ Le libellé est un vrai `<label>` : « Du » et « Au » sont indiscernables au son. */
function Borne({ libelle, value, min, max, onChange }: {
  libelle: string; value: string; min?: string; max?: string; onChange: (v: string) => void;
}) {
  return (
    <label style={{ flex: 1, minWidth: 0 }}>
      <span style={{
        display: 'block', fontSize: DATE_RANGE.bornes.labelSize, fontWeight: 600,
        color: 'var(--ink-500)', marginBottom: 4,
      }}>{libelle}</span>
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', height: DATE_RANGE.bornes.inputHeight, boxSizing: 'border-box',
          padding: `0 ${DATE_RANGE.bornes.inputPadH}px`,
          border: '1px solid var(--ink-200)', borderRadius: `var(--r-${DATE_RANGE.bornes.radius})`,
          background: 'var(--paper)', color: 'var(--ink-900)',
          fontFamily: 'var(--font-mono)', fontSize: DATE_RANGE.bornes.inputSize, outline: 'none',
        }}
      />
    </label>
  );
}
